import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, KeyRound, Mail, ShieldCheck, AlertCircle, Loader2, UserPlus } from "lucide-react";
import { studentService } from "../firebase/services";
import { isFirebaseConfigured } from "../firebase/config";

export const ActivateAccountPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(searchParams.get("token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", text: "" });
  const [activated, setActivated] = useState(false);

  useEffect(() => {
    const raw = sessionStorage.getItem("kt_last_reg");
    if (raw) {
      try {
        const info = JSON.parse(raw);
        if (!email && info.email) setEmail(info.email);
        if (!token && info.activationToken) setToken(info.activationToken);
      } catch (e) {
        console.warn(e);
      }
    }
  }, [email, token]);

  const handleActivate = async (e) => {
    e.preventDefault();
    setStatus({ type: "", text: "" });
    if (!isFirebaseConfigured) return setStatus({ type: "error", text: "Firebase is not configured yet." });
    if (!token.trim()) return setStatus({ type: "error", text: "Enter your activation code." });
    if (!email.trim()) return setStatus({ type: "error", text: "Enter the email used during registration." });
    if (password.length < 8) return setStatus({ type: "error", text: "Password must be at least 8 characters." });
    if (password !== confirmPassword) return setStatus({ type: "error", text: "Passwords do not match." });

    setLoading(true);
    try {
      const result = await studentService.activateAccount(token, email, password);
      setActivated(true);
      setStatus({ type: "success", text: `Account activated successfully. Student ID: ${result.studentId}` });
      sessionStorage.removeItem("kt_last_reg");
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      console.error("Account activation error:", err);
      if (err.code === "auth/email-already-in-use") {
        setStatus({ type: "error", text: "An account already exists for this email. Please use Login or Forgot Password." });
      } else {
        setStatus({ type: "error", text: err.message || "Could not activate your account." });
      }
    } finally {
      setLoading(false);
    }
  };

  if (activated) {
    return (
      <div className="min-h-screen bg-background py-16 px-4 flex items-center justify-center">
        <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 shadow-xl text-center space-y-5">
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-400" />
          <h1 className="text-2xl font-bold">Account Activated</h1>
          <p className="text-sm text-muted-foreground">Your student portal account is ready. Redirecting you to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-16 px-4 flex items-center justify-center">
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 shadow-xl space-y-6">
        <div className="text-center space-y-2">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 mx-auto flex items-center justify-center">
            <UserPlus className="w-6 h-6" />
          </div>
          <h1 className="text-2xl font-bold">Activate Student Account</h1>
          <p className="text-xs text-muted-foreground font-mono">Use the private activation code shown after your registration was submitted.</p>
        </div>

        {status.text && (
          <div className={`p-3.5 rounded-lg text-xs flex items-start gap-2 ${status.type === "success" ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400" : "bg-destructive/10 border border-destructive/30 text-destructive"}`}>
            {status.type === "success" ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
            <span>{status.text}</span>
          </div>
        )}

        <form onSubmit={handleActivate} className="space-y-4">
          <div>
            <label className="block text-xs font-mono font-medium mb-1">Activation Code</label>
            <input value={token} onChange={(e) => setToken(e.target.value)} required placeholder="Paste your private activation code" className="w-full px-3 py-2 text-xs rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono" />
          </div>
          <div>
            <label className="block text-xs font-mono font-medium mb-1">Registered Email</label>
            <div className="relative"><Mail className="w-4 h-4 text-muted-foreground absolute left-3 top-2.5" /><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500" /></div>
          </div>
          <div>
            <label className="block text-xs font-mono font-medium mb-1">Create Password</label>
            <div className="relative"><KeyRound className="w-4 h-4 text-muted-foreground absolute left-3 top-2.5" /><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500" /></div>
          </div>
          <div>
            <label className="block text-xs font-mono font-medium mb-1">Confirm Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} className="w-full px-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <button type="submit" disabled={loading} className="w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-mono text-sm font-semibold flex items-center justify-center gap-2">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Activating...</> : <><ShieldCheck className="w-4 h-4" />Activate Account</>}
          </button>
        </form>

        <div className="text-center text-xs text-muted-foreground font-mono">
          Already activated? <Link to="/login" className="text-emerald-400 hover:underline">Go to Login</Link>
        </div>
      </div>
    </div>
  );
};
